import { z } from 'zod'

import { ulidSchema } from '../pagination'

/**
 * Every primary key in the system is a ULID (see `pagination.ts` for the
 * pattern and why cursors are ULIDs rather than offsets). `idSchema` is the
 * name to reach for in a payload; `ulidSchema` stays the low-level one.
 */
export const idSchema = ulidSchema
export type Id = z.infer<typeof idSchema>

/**
 * Optimistic-concurrency token (§5.3). Every mutable resource returns it; a
 * `PATCH` may send it back as `If-Match` and gets `409 CONFLICT` if stale.
 * It is an integer, not an ETag hash, so a human can read it in a log.
 */
export const versionSchema = z.number().int().nonnegative()

/**
 * The `Idempotency-Key` header on every creating `POST` — the client-generated
 * ULID of the row it is about to create. A replay returns the original `201`
 * body, which is what makes the mobile retry queue safe on a mandi connection.
 */
export const idempotencyKeySchema = ulidSchema

/** A tenant slug: lowercase kebab, used in console URLs and the login body. */
export const slugSchema = z
  .string()
  .min(2)
  .max(60)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and hyphens.')

/** E.164, as the OTP table stores it. */
export const phoneSchema = z.string().regex(/^\+[1-9]\d{7,14}$/, 'Use a full phone number.')

/** A per-tenant username. Lowercased by the server; phone numbers are allowed. */
export const usernameSchema = z
  .string()
  .min(3)
  .max(40)
  .regex(/^[a-z0-9._+-]+$/i, 'Letters, numbers, dot, underscore or hyphen.')

/** Free-text search box (`?q=`). Trimmed, bounded, never a query DSL (§5.3). */
export const searchQuerySchema = z.string().trim().min(1).max(120)

/** A note the trader typed. Bounded so a paste cannot become a payload. */
export const noteSchema = z.string().trim().max(2000)
export const labelSchema = z.string().trim().min(1).max(120)
